import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  CheckCircle2, 
  Users, 
  TestTubes, 
  Truck, 
  Package, 
  Globe, 
  ClipboardCheck,
  Award
} from 'lucide-react';

const QualityPage = () => {
  const qualitySteps = [
    {
      icon: Users,
      title: 'Vendor Qualification',
      description: 'Rigorous evaluation of manufacturing capabilities, certifications (ISO 9001, ISO 14001), and production capacity before onboarding any supplier.'
    },
    {
      icon: TestTubes,
      title: 'Batch-Level Quality Checks',
      description: 'Every batch undergoes laboratory testing for chemical composition, physical properties, and purity levels. Only certified batches are released for dispatch.'
    },
    {
      icon: ClipboardCheck,
      title: 'Documentation & Compliance',
      description: 'Complete traceability with batch numbers, test certificates, Material Safety Data Sheets (MSDS), and compliance documents for all products.'
    },
    {
      icon: Package,
      title: 'Packaging Standards',
      description: 'Food-grade HDPE/PP bags with proper labeling, moisture protection, and tamper-proof sealing. Custom packaging available for specific requirements.'
    },
    {
      icon: Truck,
      title: 'Logistics Coordination',
      description: 'Strategic partnerships with reliable transporters. GPS-enabled tracking, proper loading supervision, and timely delivery across India.'
    },
    {
      icon: Globe,
      title: 'Pan-India Supply Network',
      description: 'Established supply chain covering major industrial hubs with local dispatch points and regional inventory management.'
    }
  ];

  const processHighlights = [
    'Vendor audits conducted annually',
    'Third-party lab testing facility tie-ups',
    'Batch-wise COA (Certificate of Analysis)',
    'Moisture-controlled storage facilities',
    'Regular inventory turnover management',
    'Customer-specific quality parameters'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Quality & Supply Chain</h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Systematic procurement, stringent quality control, and reliable logistics for consistent supply
            </p>
          </div>
        </div>
      </section>

      {/* Quality Process */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Quality Assurance Process</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Six-pillar framework ensuring product consistency and supply reliability
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {qualitySteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Card key={index} className="hover:shadow-xl transition-all duration-300 border-2">
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                        <Icon className="w-8 h-8 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{step.description}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Highlights */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-slate-900 mb-6">Operational Excellence</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Our supply chain operations are designed for transparency, consistency, and scalability. 
                  We maintain documented processes for every stage—from vendor selection to final delivery.
                </p>
                <div className="space-y-3">
                  {processHighlights.map((highlight, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700 font-medium">{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1768128834406-f5b1b962af9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwY2hlbWljYWwlMjBwbGFudHxlbnwwfHx8fDE3NzE4NTU2NjB8MA&ixlib=rb-4.1.0&q=85"
                  alt="Quality control"
                  className="rounded-lg shadow-xl w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testing & Certification */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-slate-900 mb-12 text-center">Testing & Certification</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <Award className="w-10 h-10 text-blue-600 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-4">Testing Parameters</h3>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Chemical composition analysis</li>
                        <li>• Particle size distribution</li>
                        <li>• Whiteness & brightness index</li>
                        <li>• Moisture content</li>
                        <li>• pH value testing</li>
                        <li>• Heavy metal screening</li>
                        <li>• Bulk density measurement</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <ClipboardCheck className="w-10 h-10 text-blue-600 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-4">Documentation Provided</h3>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Certificate of Analysis (COA)</li>
                        <li>• Material Safety Data Sheet (MSDS)</li>
                        <li>• ISO certificates (where applicable)</li>
                        <li>• Batch traceability documents</li>
                        <li>• Transport insurance papers</li>
                        <li>• GST-compliant invoicing</li>
                        <li>• Custom certifications (on request)</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Supply Chain Network */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">Pan-India Supply Capability</h2>
            <p className="text-gray-700 mb-8 leading-relaxed">
              With our registered office in Dehradun and corporate office in Saharanpur, we maintain 
              strategic access to northern India's industrial belt while serving customers across the country.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <Card className="border-2">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">7-15</div>
                  <p className="text-gray-700 font-semibold">Days Average Lead Time</p>
                </CardContent>
              </Card>
              <Card className="border-2">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">Pan-India</div>
                  <p className="text-gray-700 font-semibold">Delivery Network</p>
                </CardContent>
              </Card>
              <Card className="border-2">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">1 MT+</div>
                  <p className="text-gray-700 font-semibold">Minimum Order Quantity</p>
                </CardContent>
              </Card>
            </div>

            <div className="bg-blue-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Inventory Planning</h3>
              <p className="text-gray-700 leading-relaxed">
                We work with customers to understand their consumption patterns and maintain buffer 
                stocks for critical materials. Our inventory planning ensures you never face supply 
                disruptions during peak production cycles.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Experience Our Quality Standards</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Request samples and documentation to evaluate our products for your application
          </p>
          <a href="/contact" className="inline-block">
            <button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded text-lg font-semibold transition-colors">
              REQUEST SAMPLES & DOCUMENTATION
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default QualityPage;
